import { useState, useRef, useEffect, MouseEvent, ReactNode } from 'react';
import { Phone, Copy, Check } from 'lucide-react';

interface PhoneNumberProps {
  phone: string | null | undefined;
  /** Wrap the number in a tel: link so taps initiate a call. Default true. */
  callable?: boolean;
  /** Show a leading phone icon (Lucide). Default false — caller usually controls icon. */
  icon?: boolean;
  /** Pixel size for the leading icon when icon=true. Default 14. */
  iconSize?: number;
  /** className applied to the wrapper span. */
  className?: string;
  /** Optional className applied just to the number text. */
  numberClassName?: string;
  /** Hide copy button until hover/focus. Useful in dense list rows. */
  hideCopyUntilHover?: boolean;
  /** Optional content rendered after the number (e.g. "(Secondary)"). */
  suffix?: ReactNode;
  /** Optional aria/test id passthrough. */
  'data-testid'?: string;
}

/**
 * Renders a phone number that is always selectable AND has a one-click copy button.
 *
 * Why this component exists:
 *   - Several pages live inside containers that set `select-none` on a wrapping
 *     element (e.g. SchedulerV3 disables text selection so job-block drag works).
 *     Wrapping the phone in a `select-text` span overrides that for the number only.
 *   - On mobile, tapping a `<a href="tel:">` immediately calls — there's no easy way
 *     to select-and-copy. The dedicated copy button gives a one-tap escape hatch.
 *   - `onMouseDown` stops propagation so drag-to-select inside SchedulerV3 panels
 *     doesn't trigger the underlying drag handler.
 */
export function PhoneNumber({
  phone,
  callable = true,
  icon = false,
  iconSize = 14,
  className = '',
  numberClassName = '',
  hideCopyUntilHover = false,
  suffix,
  ...rest
}: PhoneNumberProps) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  if (!phone) return null;

  const handleCopy = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(phone);
        ok = true;
      } else {
        const ta = document.createElement('textarea');
        ta.value = phone;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch (err) {
      console.warn('Phone copy failed', err);
    }
    if (ok) {
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 1500);
    }
  };

  const numberNode = (
    <span className={`select-text whitespace-nowrap ${numberClassName}`}>{phone}</span>
  );

  return (
    <span
      className={`group inline-flex items-center gap-1.5 select-text ${className}`}
      onMouseDown={(e) => e.stopPropagation()}
      {...rest}
    >
      {icon && (
        <Phone
          className="shrink-0 opacity-70"
          style={{ width: iconSize, height: iconSize }}
          aria-hidden
        />
      )}
      {callable ? (
        <a
          href={`tel:${phone}`}
          onClick={(e) => e.stopPropagation()}
          className="select-text hover:underline"
        >
          {numberNode}
        </a>
      ) : (
        numberNode
      )}
      {suffix}
      <button
        type="button"
        onClick={handleCopy}
        onMouseDown={(e) => e.stopPropagation()}
        className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded transition-all ${
          hideCopyUntilHover
            ? 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
            : 'opacity-50 hover:opacity-100'
        } ${
          copied
            ? 'opacity-100 text-emerald-500 bg-emerald-500/10'
            : 'hover:bg-black/10 dark:hover:bg-white/10'
        }`}
        aria-label={copied ? `Phone number ${phone} copied` : `Copy phone number ${phone}`}
        title={copied ? 'Copied!' : 'Copy phone number'}
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </span>
  );
}

interface CopyPhoneButtonProps {
  phone: string;
  /** Pill-style content. Defaults to "Copy" + icon. */
  label?: string;
  /** Override className for the pill. */
  className?: string;
}

/**
 * Standalone copy-phone pill — pair this with an existing tel: button
 * when you want to keep an existing call/sms pill design and just add copy
 * as a separate action.
 */
export function CopyPhoneButton({
  phone,
  label = 'Copy',
  className = 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-500/10 hover:bg-slate-500/20 border border-slate-500/20 text-xs font-medium text-slate-400 transition-colors',
}: CopyPhoneButtonProps) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  if (!phone) return null;

  const handleCopy = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(phone);
        ok = true;
      } else {
        const ta = document.createElement('textarea');
        ta.value = phone;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch (err) {
      console.warn('Phone copy failed', err);
    }
    if (ok) {
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      onMouseDown={(e) => e.stopPropagation()}
      title={copied ? 'Copied!' : `Copy ${phone}`}
      aria-label={copied ? `Phone number ${phone} copied` : `Copy phone number ${phone}`}
      className={className}
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : label}
    </button>
  );
}
